/**
 * "Start from a design" — the recipe DATA, in one file (band designs W4).
 *
 * A recipe is a ring SET made only of ordinary controls: `rings[0]` is the
 * ring with stones, `rings[1]` the optional partner, `layout` how the pair is
 * posed. Every field is a `RingDesignPatch` field, so applying one is the same
 * thing as moving the sliders — everything stays editable afterwards.
 *
 * Rules (plan § H, refs/README.md):
 *   · plain, brand-neutral names; no brand, maker, collection or mark anywhere;
 *   · no wearer field (ring size, engraving text, the head, ring labels) —
 *     the applier keeps the wearer's own (`recipes.ts`);
 *   · thumbnails are `public/ring-designs/<id>.webp`, rendered offline; until a
 *     file exists the card draws `recipeThumbSvg` instead.
 *
 * Seeded from `band-designs-handoff/w1/recipes-w1.json` (refs 1, 5, 6, 7, the
 * headless-verified W1 recipes) with the W2–W5 lanes' findings applied: the
 * parametric "param" section for the slim comfort domes (W1 finding 6),
 * "close" spacing and fine beads for the tight pavé / shared claws (findings
 * 1–2), the "catalogue" pose (finding 4), and — after the W2–W5 repair — the
 * "standing" pair for the photos that stand both rings on their edge (refs 1,
 * 3, 4, 9), the per-stone diagonal squash (refs 3, 9) and the catalogue golds. Refs 2, 3, 4, 8 and 9 are first
 * drafts on the stone-pattern library's reference entries; the recipe agent
 * tunes all nine against the photos.
 */
import { partialStepGrooves, stonePatternFromLibrary } from "../../../shared/api-contract";
import type { BandLayoutPose, BandLayoutPreset, ParamProfile, RingDesignPatch, StonePattern, StonePatternLayer } from "../../../shared/api-contract";
import type { RecipeLookId } from "./looks";

export interface RingRecipe {
  /** Stable forever: thumbnails and analytics name it. */
  id: string;
  name: string;
  /** One line under the name. */
  blurb: string;
  /** The ring with stones, then the optional partner. */
  rings: [RingDesignPatch] | [RingDesignPatch, RingDesignPatch];
  layout: BandLayoutPreset;
  /**
   * v68: the photograph's own pose — `layout`'s law overridden per ring and per
   * camera (`setBandLayout(layout, pose)`, caps `ringPoseV68`). A set-level
   * fact, never inside `rings[]`. Absent ⇒ the preset's shared law (today).
   */
  pose?: BandLayoutPose;
  /** v68: the partner's size, US sizes above ring 0's, when the recipe makes
   * the partner (`recipes.ts`). Absent ⇒ `PARTNER_SIZE_STEP_US` (2.5, today). */
  partnerSizeDeltaUS?: number;
  /** v68: the light / tone / floor the photo was taken under (`looks.ts`,
   * caps `ringLookV68`). Absent ⇒ "studio", so a recipe always lands on the
   * same look whatever came before, and refs without one render as before. */
  look?: RecipeLookId;
  /** What the controls cannot yet reproduce from the photo (for the team, not shown). */
  missing?: string;
}

/**
 * A library entry with its first layer re-tuned for this photo. The entry's
 * `libraryId` is kept, so the pattern editor marks where the recipe started.
 */
const fromLibrary = (id: string, layer: Partial<StonePatternLayer>): StonePattern => {
  const p = stonePatternFromLibrary(id);
  if (!p) throw new Error(`recipeData: no stone-pattern library entry "${id}"`);
  return { ...p, layers: [{ ...p.layers[0], ...layer }, ...p.layers.slice(1)] };
};

/**
 * Metal colours. The yellow photos are a PALE 18 ct-style yellow and the rose
 * photos a soft pink; the "yellowGold"/"roseGold" presets render visibly more
 * orange/salmon next to them (measured: rendered r/g 1.20 vs the photos' 1.10,
 * b/g 0.70 vs 0.78). The viewer now has the two catalogue golds as PRESETS
 * ("paleYellowGold" #F2D296, "softRoseGold" #E8BA9C — measured 1.11 / 0.76 and
 * 1.19 / 0.85 against the photos' 1.11 / 0.76 and 1.18 / 0.86; W3 re-measured
 * the yellow as the mean of refs 1, 3, 4, 6, 8), so a recipe is one ordinary metal chip with
 * no colour override (the Material tab used to read "Yellow Gold" over a
 * custom colour).
 */
const metal = (presetId: string) => [{ presetId, finishId: "polished" }];
const YELLOW = metal("paleYellowGold");
const ROSE = metal("softRoseGold");
const WHITE = metal("whiteGold");

/** The slim comfort dome with fully rounded edges (refs 1–3, 6, 8, 9; W2 body lane). */
const DOME = (crownMM: number, edgeRadiusMM = 0.6): Partial<ParamProfile> =>
  ({ crownMM, edgeRadiusMM, comfortMM: 0.25, flatTop: 0, bevelMM: 0 });
const FLAT_CRISP: Partial<ParamProfile> = { crownMM: 0, flatTop: 1, edgeRadiusMM: 0.05, comfortMM: 0.15, bevelMM: 0 };
const FLATTISH: Partial<ParamProfile> = { crownMM: 0.15, flatTop: 0.3, edgeRadiusMM: 0.35, comfortMM: 0.2, bevelMM: 0 };

/** A fine V line all the way round (refs 1, 2, 7). */
const fineLine = (id: string, positionMM: number) => ({
  id, direction: "horizontal" as const, positionMM, widthMM: 0.2, depthMM: 0.12, jointType: "v60" as const, vAngleDeg: 90,
});

/** A deep, narrow hairline (ref 1): a 30° V 0.15 mm wide (so 0.28 mm deep).
 * Critic round 1 drew it "shadow" (a 90° V had read as a double line); critic
 * round 2 found that, under the catalogue look's SSAO, too dark where the
 * photo's lines are mid-grey (L 82 against 143 below the row), so each line
 * now takes the finish that measured closest. Absent ⇒ polished. */
const narrowV = (id: string, positionMM: number, surface?: "matt") => ({ ...fineLine(id, positionMM), widthMM: 0.15, depthMM: 0.28, vAngleDeg: 30, ...(surface ? { surface } : {}) });

/** The same narrow V (0.15 mm, 30°) along an open PATH on the unrolled face (v66 "path"
 * groove, the stone pattern's own plane: arc 0 is the row's centre) — a line
 * that starts where a stone row ends and runs on round the ring. Matt since
 * critic round 2: the photo's line there is a soft mid-grey (L 170), a shadow
 * cut read L 98–132. */
const pathHairline = (id: string, yMM: number, fromArcMM: number, toArcMM: number) => ({
  id, direction: "path" as const, positionMM: 0, widthMM: 0.15, depthMM: 0.28, jointType: "v60" as const, vAngleDeg: 30, surface: "matt" as const,
  path: { points: [{ arcMM: fromArcMM, yMM }, { arcMM: toArcMM, yMM }] },
});

/** Ref 1: which profile-Y sign is the camera-facing (slit-side) edge of a "standing" ring. */
const S1 = 1;

/** A plain band: no stones, no grooves. Every recipe ring starts from it. */
const band = (p: RingDesignPatch): RingDesignPatch => ({
  profileId: "param", metalKarat: "gold14k", edgeStyle: "sharp", grooves: { pattern: "none" },
  diamond: { style: "none" }, designGrooves: [], ...p,
});

/**
 * Neutral marks for the inside-the-band logo (refs 8, 9). Our own simple
 * shapes — never the photo's maker's mark. "with-text" puts them before the
 * wearer's inscription, or alone when there is none.
 */
const STAR_MARK = "M0 -10 L2.9 -4 L9.5 -3.1 L4.7 1.5 L5.9 8.1 L0 5 L-5.9 8.1 L-4.7 1.5 L-9.5 -3.1 L-2.9 -4 Z";
const LEAF_MARK = "M0 -10 C6 -6 6 4 0 10 C-6 4 -6 -6 0 -10 Z";
const logo = (svg: string, color: string, heightMM = 1.6) => ({
  // angle 0 puts the mark inside the band under the stones' arc, the side a
  // catalogue camera looks into; "center" sits it mid-width like the photos'.
  angleDeg: 0, side: "center" as const, color,
  logo: { svg, heightMM, mode: "with-text" as const },
});

export const RECIPES: readonly RingRecipe[] = [
  // v68 photo match (band-designs-handoff/w4/ref-1/PLAN.md, then tuned by the
  // w4 meters through the gallery, then the critic's round-1 findings). The
  // photo is a long-lens shot (fov ~5°) of two rings stood almost edge-on (3-D
  // fit turn 78.5° / 79.2°), leaning together (+24.8° / −19.9° on screen); the
  // partner a size bigger and heavier (outer diameter 1.24× ring 0's).
  // Ring 0: a slim 2.4 × 1.7 court dome. 1.6, not the fit's 1.5: the photo's
  // band ends read ~5% thicker than ours did (critic), the inner/outer ratios
  // moved toward the photo's, and at 1.6 the CNC machines the ten seats (at
  // 1.5 it left them unplanned: 0.55 mm of wall under each, its minimum 0.6).
  // 1.7 (and the partner 2.85) since critic round 2: our openings were still
  // 2–5 % larger than the photo's (inner/outer major 0.629 / 0.644 → 0.624 /
  // 0.632 against 0.615 / 0.622).
  // Ten 1.4 mm stones at the photo's pitch (gap 0.10, gap/d 0.07 as measured:
  // the photo's stones nearly touch). Critic round 2: the photo's row runs OVER
  // the camera-facing (slit-side) edge — its stone centres sit 0.575 of A's
  // semi-minor in from the far silhouette, ours sat at 0.433 with polished
  // metal beyond them. `seatRimMM` −0.45 (v68: a seat may now reach past the
  // outer face, cut on the face only) and the row centred at +0.72 mm put them
  // at 0.58: their girdles stand over the rounded edge as the photo's do. A
  // square-ended plate recess (0.08 deep, 0.05 clearance) holds them, open at
  // that edge; tables 0.05 mm under the crest, tiny base beads (0.085 d) on
  // the groove side of each junction only (the photo's gaps on the slit side
  // read dark, its beads show on the groove side), no end beads (v68
  // junctionOptions).
  // Lines (critic round 2, measured on the critic's rectified arm at the row
  // and below it): the channel line on the recess wall, all the way round
  // (narrow 30° V, polished); a fainter line out toward the far edge (polished
  // 90° V at −0.58 — at the row it lands where the photo's mid line is, below
  // the row it reads as faint as the photo's); and the photo's line that runs
  // on from each end of the row along the stones' own track, as two open matt
  // path grooves. The earlier pair at −0.5 / −0.82 sat on the far edge's round
  // and read as two heavy pinstripes on the silhouette.
  // Partner: a 2.5 × 2.85 court band with one narrow matt V at −0.17 mm,
  // toward the FAR edge — a single mid-grey line in from the outer silhouette
  // where the photo has it (L 145–172 against 149–159).
  // "+e" (the camera-facing, slit-side edge) is profile +Y × S1 — S1 = +1,
  // confirmed by render (a +Y groove lands on the slit side of both).
  {
    id: "ref-1",
    missing: "The photo's stones read whiter and milkier (gem shader and diamond light left alone); its thin black slivers are the rings reflecting each other, which an environment cannot place, so ours are fewer and broader; the stone ring's inside reads ~4 L* darker; its highlight band beside the row is brighter.",
    name: "Pavé line beside a fine groove",
    blurb: "Ten small pavé stones in a recess along one edge of a slim dome, two fine lines beside them; a heavier domed partner with one fine line.",
    layout: "standing",
    pose: {
      camera: { elevationDeg: 21, azimuthDeg: 0, fovDeg: 5, fill: 0.81, center: [0.479, 0.47] },
      // critic round 2: ring 0 leans 0.4° more, the partner 0.4° more and 0.015 D0 closer, so A's
      // upper arc tucks in front of B as in the photo (meter at 600 px: overlap 0.00044 H², interface
      // 0.24 H, against the photo's 0.00104 / 0.257 — it was 0 / 0.065; centre offset 0.486 / −0.140,
      // the photo's exactly; screen leans 24.95° / −20.13° against 24.8° / −19.9°)
      rings: [{ turnDeg: 77.7, leanDeg: 20.6, spinDeg: 1 }, { turnDeg: 78.5, leanDeg: 16.3, sideD0: 0.495, depthD0: 0.11 }],
    },
    partnerSizeDeltaUS: 3.5,
    look: "catalogue-white",
    rings: [
      band({
        // critic round 3: a real comfort-fit bore (0.35, was 0.05) — the photo's insides read ROUNDED,
        // their reflections squeezed into thin streaks, where a near-flat bore laid the tent's cards out
        // as wide flat parallelograms with a crease at the inner edge. 1.8 thick (was 1.7) holds the
        // inner/outer ratio the comfort dome opens (0.640 → 0.630 against the photo's 0.615).
        paramProfile: { crownMM: 0.45, flatTop: 0, edgeRadiusMM: 0.5, comfortMM: 0.35, bevelMM: 0 },
        widthMM: 2.4, thicknessMM: 1.8, materials: YELLOW,
        diamond: { style: "pattern", stoneShape: "round", stoneSizeMM: 1.4, depthOffsetMM: 0.27, junctionScale: 0.5, junctionOptions: { ends: false, grow: false, sides: S1 > 0 ? "-y" : "+y" } },
        // Critic round 3, the setting: the photo's stones sit in one sunken strip with a dark line hugging
        // the groove-side girdles; ours sat in cups, a bright seat wall showing above every girdle on the
        // groove side (the dome there rises 0.16 mm above the stone's centre, and the 0.08 plate left
        // ~0.3 mm of bore wall in view). The plate is now 0.3 deep, so the girdles meet its floor, and
        // the stones are raised back by +0.27 (depthOffsetMM is + = up): tables 0.043 under the crest,
        // as before. Groove-side girdle at −0.085 as before (1.4 mm at +0.615). Gap 0.12 (pitch 1.52,
        // the photo's 1.47 on the meter's scale): at 0.04–0.08 the faceted stones ran together and the
        // stone meter read one blob for five of them.
        stonePattern: {
          ...fromLibrary("offset-pave-rail-9", {
            count: 10, sizeMM: 1.4, gapMM: 0.12, centerYMM: S1 * 0.615, setting: "pave",
            pocket: { shape: "plate", per: "layer", depthMM: 0.3, clearanceMM: 0.05, cornerRadiusMM: 0.05 }, rails: undefined,
          }),
          // the row runs OVER the camera-facing edge (critic round 2): each seat is cut on the face only
          seatRimMM: -0.45,
        },
        designGrooves: [
          // the channel line, all the way round, on the plate's groove-side wall right beside the
          // girdles (critic round 3: at 600 px the photo reads L* 64 there, ours now 65; drawn "shadow"
          // — polished, it read as a lit gold lip)
          { ...narrowV("line-2", -S1 * 0.17), surface: "shadow" as const },
          // one fine line toward the far edge (critic round 3: the 90° V at −0.58 showed both lit walls
          // and read as stepped rails; a single narrow 30° V)
          narrowV("line-1", -S1 * 0.64),
          // the photo's crisp dark line that runs on from each end of the row, along the band near its
          // slit-side edge (critic round 3: L* 18–24 at s 0.82–0.84; the matt hairline read L* 72):
          // a wider, deeper V drawn "shadow", from just past each end stone round the ring
          { ...pathHairline("line-3a", S1 * 0.85, 7.0, 30), surface: "shadow" as const, widthMM: 0.2, depthMM: 0.37 },
          { ...pathHairline("line-3b", S1 * 0.85, -7.0, -30), surface: "shadow" as const, widthMM: 0.2, depthMM: 0.37 },
        ],
      }),
      band({
        // critic round 3: comfort fit 0.55 (was 0.10) and 3.0 thick (was 2.85), as ring 0
        paramProfile: { crownMM: 0.85, flatTop: 0, edgeRadiusMM: 0.75, comfortMM: 0.55, bevelMM: 0 },
        widthMM: 2.5, thicknessMM: 3.0, materials: YELLOW,
        // one narrow matt V (critic round 2): the photo's line is a single mid-grey line (L 149–159,
        // prominence 51–94 in from the outer silhouette); a shadow cut read L 89–104 and the 0.25 mm
        // V as a double-walled stripe; matt 30° reads L 145–172, prominence 60–94
        designGrooves: [narrowV("line-1", -S1 * 0.17, "matt")],
      }),
    ],
  },
  {
    id: "ref-2",
    missing: "The photo's plate outline is lobed around each stone (a scalloped navette); ours is a smooth lens.",
    name: "Navette plate with three stones",
    blurb: "A sunken eye-shaped plate holding three graduated pavé stones beside a fine line; the partner carries a line.",
    layout: "catalogue",
    rings: [
      band({
        paramProfile: DOME(0.5), widthMM: 2.6, thicknessMM: 1.8, materials: ROSE,
        diamond: { style: "pattern", stoneShape: "round", stoneSizeMM: 1.4, junctionScale: 1.4 },
        stonePattern: fromLibrary("eye-navette-rail-3", {
          sizeMM: 1.35, sizing: { mode: "graduated", endScale: 0.7 }, gapMM: 0.2, setting: "pave",
          pocket: { shape: "navette", depthMM: 0.45, clearanceMM: 0.22, cornerRadiusMM: 0.3 },
          rails: { sides: "right", gapMM: 0.25, widthMM: 0.2, depthMM: 0.12, extent: "all-round" },
        }),
      }),
      band({ paramProfile: DOME(0.55), widthMM: 2.8, thicknessMM: 1.8, materials: ROSE, designGrooves: [fineLine("line-1", 0.75)] }),
    ],
  },
  {
    id: "ref-3",
    missing: "The photo's capsules have a fine raised rim; ours are plain recesses.",
    name: "Two capsules and a lone stone",
    blurb: "Seven graduated stones on a slant: three in a capsule, one alone, three in a second capsule.",
    layout: "standing",
    rings: [
      band({
        paramProfile: DOME(0.5), widthMM: 2.6, thicknessMM: 1.8, materials: YELLOW,
        // W3 (photo match): the photo's centre stone nearly fills the band and
        // the group runs along much of the front, so 1.8 mm graduated to half,
        // centred 2.5 mm down the front. A clearance over ~0.08 mm at this size
        // pushes the capsule past the domed face, where it is clipped away and
        // the troughs stop reading; 0.06 keeps both.
        diamond: { style: "pattern", stoneShape: "round", stoneSizeMM: 1.8 },
        stonePattern: fromLibrary("capsule-3-1-3-diagonal", {
          angleDeg: 3, centerArcMM: -2.5, sizeMM: 1.8, sizing: { mode: "graduated", endScale: 0.5 }, gapMM: 0.15, groupGapMM: 0.5,
          pocket: { shape: "capsule", depthMM: 0.35, clearanceMM: 0.06, minGroup: 2 },
        }),
      }),
      band({ paramProfile: DOME(0.55), widthMM: 3.0, thicknessMM: 1.8, materials: YELLOW }),
    ],
  },
  {
    id: "ref-4",
    missing: "The photo's step line hugs the row more closely (ours is one straight line with fixed exits).",
    name: "Stepped half with claw-set stones",
    blurb: "Ten claw-set stones on a lowered stretch beside a step line; a partner stepped along one edge.",
    layout: "standing",
    rings: [
      band({
        paramProfile: FLATTISH, widthMM: 3.0, thicknessMM: 1.8, materials: YELLOW,
        // The row sits wholly ON the lowered floor (every girdle inside it, so
        // every stone rests on it): the step line at −0.45 mm, the stones
        // centred 0.45 mm to the lowered side. It used to straddle the step
        // wall, every girdle half over the floor (review finding).
        diamond: { style: "pattern", stoneShape: "round", stoneSizeMM: 1.1 },
        stonePattern: fromLibrary("half-row-claws-step-10", { centerYMM: 0.45, count: 10, sizeMM: 1.1, gapMM: 0.08, setting: "shared-prong", rails: undefined }),
        designGrooves: partialStepGrooves({ side: "+y", positionMM: -0.45, depthMM: 0.2, centerArcMM: 0, lengthMM: 15, exitDeg: 35, cornerRadiusMM: 2, spanMM: 3 }, "step"),
      }),
      band({ paramProfile: { ...FLATTISH, step: { side: "+y", positionMM: 0.95, heightMM: 0.2, wallDeg: 80 } }, widthMM: 3.5, thicknessMM: 1.8, materials: YELLOW }),
    ],
  },
  {
    id: "ref-5",
    missing: "Photo bands are thinner (about 1.5 mm); 1.8 kept to stay above the daily-wear warning.",
    name: "Flat half-eternity pavé",
    blurb: "A crisp flat band with small pavé stones half way round; a flat plain partner.",
    layout: "catalogue",
    rings: [
      band({
        paramProfile: FLAT_CRISP, widthMM: 2.2, thicknessMM: 1.8, materials: WHITE,
        // W3 (photo match): fine pavé — about as many stones across the front as the photo.
        diamond: { style: "memoir", setting: "pave", spacing: "close", junctionScale: 0.7, stoneShape: "round", stoneSizeMM: 1.1, count: 0, spreadDeg: 180, placementDeg: -90, rowPosition: 0 },
      }),
      band({ paramProfile: FLAT_CRISP, widthMM: 3.0, thicknessMM: 1.8, materials: WHITE }),
    ],
  },
  {
    id: "ref-6",
    missing: "Close.",
    name: "Domed half-eternity pavé",
    blurb: "A comfort dome with small pavé stones nearly touching, half way round; a domed plain partner.",
    layout: "catalogue",
    rings: [
      band({
        paramProfile: DOME(0.45), widthMM: 3.0, thicknessMM: 1.8, materials: YELLOW,
        // W3 (photo match): ~1.2 mm stones with fine beads, nearly touching.
        diamond: { style: "memoir", setting: "pave", spacing: "close", junctionScale: 0.6, stoneShape: "round", stoneSizeMM: 1.2, count: 0, spreadDeg: 180, placementDeg: -90, rowPosition: 0 },
      }),
      band({ paramProfile: DOME(0.5), widthMM: 3.5, thicknessMM: 1.8, materials: YELLOW }),
    ],
  },
  {
    id: "ref-7",
    missing: "The photo's claws are chunkier rounded blocks on a recessed gallery; ours are slimmer.",
    name: "Shared-claw half eternity",
    blurb: "Two-millimetre stones in shared claws half way round; a flat-court partner with an edge line.",
    layout: "catalogue",
    rings: [
      band({
        paramProfile: DOME(0.4, 0.4), widthMM: 4.0, thicknessMM: 2.0, materials: WHITE,
        diamond: { style: "memoir", setting: "shared-prong", spacing: "close", junctionScale: 1.15, stoneShape: "round", stoneSizeMM: 2.0, count: 0, spreadDeg: 180, placementDeg: -90, rowPosition: 0 },
      }),
      band({
        paramProfile: { crownMM: 0.06, flatTop: 0.6, edgeRadiusMM: 0.2, comfortMM: 0.35, bevelMM: 0 }, widthMM: 4.5, thicknessMM: 1.8, materials: WHITE,
        designGrooves: [fineLine("edge-line", 1.75)],
      }),
    ],
  },
  {
    id: "ref-8",
    missing: "The photo interlocks the two rings; no layout does. Stone clusters are seeded, not the photo's exact positions.",
    name: "Star-set constellation",
    blurb: "About fifty small flush stones of mixed sizes in small clusters all the way round; a plain domed partner. A small mark inside.",
    layout: "catalogue",
    rings: [
      band({
        paramProfile: DOME(0.5), widthMM: 3.5, thicknessMM: 1.8, materials: YELLOW,
        // W3 (photo match): smaller stones in more, tighter clusters, so they
        // read all the way round as in the photo.
        diamond: { style: "pattern", stoneShape: "round", stoneSizeMM: 1.0 },
        stonePattern: fromLibrary("constellation-star-25", {
          sizeMM: 1.0, sizing: { mode: "random", min: 0.6, max: 1 }, gapMM: 0.08, groupGapMM: 0.8, seed: 21,
          groups: [2, 3, 1, 2, 3, 2, 2, 3, 1, 2, 3, 2, 1, 3, 2, 2, 3, 1, 2, 3, 2, 2, 1, 3],
        }),
        engraving: logo(STAR_MARK, "#8C7440"),
      }),
      band({ paramProfile: DOME(0.5), widthMM: 4.0, thicknessMM: 1.8, materials: YELLOW, engraving: logo(STAR_MARK, "#8C7440") }),
    ],
  },
  {
    id: "ref-9",
    missing: "The photo has the stone ring in front on the RIGHT; the standing pair puts it on the left.",
    name: "Graduated diagonal sweep",
    blurb: "Eleven graduated flush stones in a long sweep across the band; a plain domed partner. A small mark inside.",
    layout: "standing",
    rings: [
      band({
        paramProfile: DOME(0.5), widthMM: 4.0, thicknessMM: 1.8, materials: ROSE,
        diamond: { style: "pattern", stoneShape: "round", stoneSizeMM: 1.5 },
        stonePattern: fromLibrary("sweep-graduated-long-11", {
          path: "diagonal", angleDeg: 14, count: 11, sizeMM: 1.55, sizing: { mode: "graduated", endScale: 0.45 }, gapMM: 0.25,
        }),
        engraving: logo(LEAF_MARK, "#8E6352"),
      }),
      band({ paramProfile: DOME(0.5), widthMM: 4.0, thicknessMM: 1.8, materials: ROSE, engraving: logo(LEAF_MARK, "#8E6352") }),
    ],
  },
];
